/**
 * @file apps/web/src/lib/projectMembers.ts
 * @description Pure helpers behind the Project Members page and the invite
 * accept page: role labels, who may manage membership, the message shown after
 * an invite, and reading a Supabase auth error out of an invite link's URL.
 * No React and no network, so they are unit-tested directly.
 */

import type { InviteOutcome, InviteRole } from './api.js';

/** The four roles a project admin may give — the product's names, in the order shown. */
export const INVITE_ROLE_OPTIONS: { value: InviteRole; label: string; hint: string }[] = [
  { value: 'lead',         label: 'Project admin',   hint: 'Manages the project and its members' },
  { value: 'consultant',   label: 'Consultant',      hint: 'Implementation team member' },
  { value: 'client_admin', label: 'Client approver', hint: 'Can be named as an approver; signs off for the client' },
  { value: 'client_user',  label: 'Client tester',   hint: 'Runs UAT scripts for the client' },
];

const ROLE_LABEL: Record<string, string> = {
  lead: 'Project admin',
  consultant: 'Consultant',
  client_admin: 'Client approver',
  client_user: 'Client tester',
  viewer: 'Viewer',
};

export function projectRoleLabel(role: string): string {
  return ROLE_LABEL[role] ?? role;
}

export interface MemberLike {
  id: string;
  role: string;
}

/**
 * Whether to SHOW membership controls. The API is the real gate (a project
 * admin or a workspace admin/owner); this only decides what the page offers, so
 * a consultant or client user never sees buttons that would answer 403.
 * `tenantRole` is the caller's workspace role; the project role is read from
 * the member list because the session's own `role` is only their FIRST project's.
 */
export function canManageMembers(
  members: MemberLike[],
  self: { userId: string; tenantRole?: string },
): boolean {
  if (self.tenantRole === 'owner' || self.tenantRole === 'admin') return true;
  return members.some((m) => m.id === self.userId && m.role === 'lead');
}

/** True when `memberId` is the only project admin — the API refuses to demote/remove them. */
export function isOnlyProjectAdmin(members: MemberLike[], memberId: string): boolean {
  const leads = members.filter((m) => m.role === 'lead');
  return leads.length === 1 && leads[0]!.id === memberId;
}

/** The confirmation line after POST /invites, matching what actually happened. */
export function describeInviteOutcome(result: InviteOutcome, email: string): string {
  switch (result.outcome) {
    case 'linked':
      return `${email} already has a Veridox account — they've been added to the project and notified.`;
    case 'resent':
      return `${email} already had a pending invite — sent again.`;
    case 'invited':
      return result.emailSent
        ? `Invite sent to ${email}. They'll get an email to set a password and join.`
        : `Invite saved for ${email}, but no email was sent — Supabase Auth isn't configured on this server.`;
  }
}

/**
 * Supabase puts a failed invite link's reason in the URL hash
 * (#error=access_denied&error_code=otp_expired&error_description=...). Returns a
 * message fit to show, or null when the hash carries no error.
 */
export function parseAuthHashError(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  if (!params.get('error')) return null;
  if (params.get('error_code') === 'otp_expired') {
    return 'This invite link has expired or was already used. Ask your project admin to resend the invite.';
  }
  return params.get('error_description')?.replace(/\+/g, ' ') ?? 'This invite link is not valid.';
}

/** Where a freshly signed-in user lands: the client portal for client roles, the app otherwise. */
export function landingPathFor(userType: 'si' | 'client'): string {
  return userType === 'client' ? '/portal/dashboard' : '/dashboard';
}
